import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type {
  WellCompletionResponse,
  WellCompletionRow,
} from 'types/well-completions';

const WELL_COMPLETIONS_QUERY_KEY = 'well-page:completions' as const;

// One empty array for every render, so the table's rows keep their identity until the read lands.
const NO_COMPLETIONS: WellCompletionRow[] = [];

// No loading flag: the pages that hold this read pass only the rows and the error down,
// and reading isFetching would re-render the whole page as each read starts and ends.
export interface UseWellCompletions {
  /** Every completion of the well (empty while loading or on error). */
  data: WellCompletionRow[];
  /** The failed read, or null. */
  error: Error | null;
}

/**
 * Strips the leading slash from a HATEOAS href: ky is configured with a prefixUrl, so
 * request paths must not start with one.
 *
 * @param {string} href - The resource href.
 * @return {string} The request path.
 */
const toPath = (href: string) => href.replace(/^\/+/, '');

/**
 * The completions read for one well.
 *
 * @param {string} uwi - The well's 10-digit uwi.
 * @param {string} resourceHref - The `resources.queries.wellCompletions` href.
 * @return {object} The query options, keyed by href and uwi.
 */
const wellCompletionsQueryOptions = (uwi: string, resourceHref: string) =>
  queryOptions({
    queryKey: [WELL_COMPLETIONS_QUERY_KEY, resourceHref, uwi] as const,
    queryFn: async ({ signal }) => {
      const response = await api
        .post(toPath(resourceHref), {
          json: { query: { uwi } },
          signal,
        })
        .json<WellCompletionResponse>();
      return response.results;
    },
    // Nothing to read until the well and its resource graph have both resolved.
    enabled: Boolean(uwi) && Boolean(resourceHref),
    // Completions change only on a Kafka ingest, like perforations.
    staleTime: 300_000,
  });

/**
 * Every completion of the well, for the dates on the Wellbore Completions
 * summary rows. Same shape as the perforations read in
 * `components/well-page-content`.
 *
 * @param {string} uwi - The well's 10-digit uwi.
 * @param {string} resourceHref - The `resources.queries.wellCompletions` href.
 * @return {UseWellCompletions} The well's completions and the read's state.
 */
export function useWellCompletions(
  uwi: string,
  resourceHref: string
): UseWellCompletions {
  const { data, error } = useQuery(
    wellCompletionsQueryOptions(uwi, resourceHref)
  );

  return {
    data: data ?? NO_COMPLETIONS,
    error: error ?? null,
  };
}
